import React, { Fragment, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PencilIcon, PlusIcon, TableCellsIcon, TrashIcon } from '@heroicons/react/24/outline'
import {
  addRow,
  deleteColumn,
  deleteRow,
  selectData,
} from '../../../../../../../reducers/meeting-type-task/constructor'
import { Menu, Transition } from '@headlessui/react'
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid'
import RenameSectionForm from '../rename-section-form/RenameSectionForm'
import { classNames } from '../../../../../../../utils/className'
import { ChevronDownIcon } from '@heroicons/react/16/solid'
import UpdateColumnForm from './update-column-form/UpdateColumnForm'
import AddColumnForm from './add-column-form/AddColumnForm'
import {
  selectOpenForm as selectUpdateColumnOpenForm,
  setOpenForm as setOpenUpdateColumnForm,
} from '../../../../../../../reducers/meeting-type-task/update-column'
import { setOpenForm as setOpenUpdateCellContentForm } from '../../../../../../../reducers/meeting-type-task/update-cell-content'

export default function TableSection({
  section,
  index,
  saveContent,
  deleteSection,
  renamingSectionUuid,
  setRenamingSectionUuid,
}) {
  const dispatch = useDispatch()

  const showUpdateColumnForm = useSelector(selectUpdateColumnOpenForm)
  const { sections } = useSelector(selectData)

  const [showColumnDetails, setShowColumnDetails] = useState([])

  const toggleColumnDetails = (event, sectionIndex, columnIndex) => {
    event.preventDefault()
    let newData = [...showColumnDetails]
    if (showColumnDetails.includes(`section-${sectionIndex}-col-${columnIndex}`)) {
      const indexForDelete = showColumnDetails.findIndex(
        (item) => item === `section-${sectionIndex}-col-${columnIndex}`,
      )
      if (indexForDelete !== -1) {
        newData.splice(indexForDelete, 1)
      }
    } else {
      newData.push(`section-${sectionIndex}-col-${columnIndex}`)
    }

    setShowColumnDetails(newData)
  }

  const openUpdateCellContentForm = (sectionIndex, rowIndex, colIndex, colName, content) => {
    dispatch(
      setOpenUpdateCellContentForm({
        sectionIndex,
        rowIndex,
        colIndex,
        colName,
        content,
        openForm: true,
      }),
    )
  }

  const showUpdateColForm = (sectionIndex, colIndex, name, type, resultType) => {
    dispatch(
      setOpenUpdateColumnForm({
        openForm: `section-${sectionIndex}-column-${colIndex}`,
        name,
        type,
        resultType,
      }),
    )
  }

  const addNewRowProcess = (event, sectionIndex) => {
    event.preventDefault()
    const cells = []

    const section = sections[sectionIndex]

    section.data.cols.forEach((col) => {
      const cell = {
        assignment: '',
        type: col.type,
      }
      if (col.type === 'ai-task') {
        cell.resultType = col.resultType
      }

      cells.push(cell)
    })

    dispatch(addRow({ index: sectionIndex, row: { cells } }))
  }

  const deleteColProcess = (event, sectionIndex, indexForDelete) => {
    event.preventDefault()
    dispatch(deleteColumn({ sectionIndex, indexForDelete: indexForDelete }))
  }

  const deleteRowProcess = (event, sectionIndex, indexForDelete) => {
    event.preventDefault()
    dispatch(deleteRow({ sectionIndex, indexForDelete: indexForDelete }))
  }

  return (
    <div>
      <div className={'mb-4 flex flex-row justify-between'}>
        <div className={'flex items-center text-sm text-gray-500'}>
          <TableCellsIcon className={'w-4 h-4 text-gray-500 mr-4'} />
          {renamingSectionUuid === section.uuid && (
            <RenameSectionForm section={section} sectionIndex={index} saveContent={saveContent} />
          )}
          {renamingSectionUuid !== section.uuid && (
            <>
              {section.name || 'Table section'}
              <button
                type={'button'}
                onClick={(event) => {
                  setRenamingSectionUuid(section.uuid)
                }}
                className={'ml-1 p-1 rounded-md hover:bg-indigo-50'}
              >
                <PencilIcon className={'w-4 h-4 text-gray-400'} />
              </button>
              <span className={'pl-8 text-gray-300 text-sm'}>{section.uuid}</span>
            </>
          )}
        </div>
        <div>
          <Menu as="div" className="relative flex-none">
            <Menu.Button className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
              <span className="sr-only">Open options</span>
              <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                <Menu.Item>
                  {({ focus }) => (
                    <button
                      onClick={() => {
                        deleteSection(index)
                      }}
                      className={classNames(
                        focus ? 'bg-gray-50' : '',
                        'block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900',
                      )}
                    >
                      Delete section
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
      <div className={'space-y-4'}>
        <div className={'flex flex-row'}>
          <div
            className={classNames(
              `grid gap-2`,
              section.data.cols.length > 0 && `w-full mr-4`,
              section.data.cols.length === 1 && `grid-cols-1`,
              section.data.cols.length === 2 && `grid-cols-2`,
              section.data.cols.length === 3 && `grid-cols-3`,
              section.data.cols.length === 4 && `grid-cols-4`,
              section.data.cols.length === 5 && `grid-cols-5`,
              section.data.cols.length === 6 && `grid-cols-6`,
            )}
          >
            {section.data.cols.map((col, colIndex) => (
              <div key={`column-${colIndex}`} className={'col-span-1 p-2 rounded-md ring-1 ring-gray-200'}>
                <div className={'flex flex-row  justify-between items-center'}>
                  <div className={'flex flex-row items-center overflow-hidden'}>
                    <button
                      type={'button'}
                      onClick={(event) => {
                        toggleColumnDetails(event, index, colIndex)
                      }}
                      className={
                        'flex mr-2 w-5 h-5 hover:bg-indigo-50 rounded-md content-center items-center justify-center transition-all'
                      }
                    >
                      <ChevronDownIcon className={'w-4 h-4 text-gray-500'} />
                    </button>
                    <div className={'text-sm text-gray-700 font-semibold'}>{col.name}</div>
                  </div>
                  <Menu
                    as="div"
                    key={`col-options-${colIndex}-${(Math.random() + 1).toString(36).substring(7)}`}
                    className="relative flex-none"
                  >
                    <Menu.Button className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
                      <span className="sr-only">Open options</span>
                      <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                        <Menu.Item>
                          {({ focus }) => (
                            <button
                              onClick={(event) => {
                                showUpdateColForm(index, colIndex, col.name, col.type, col.resultType)
                              }}
                              className={classNames(
                                focus ? 'bg-gray-50' : '',
                                'block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900',
                              )}
                            >
                              Update column
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ focus }) => (
                            <button
                              onClick={(event) => {
                                deleteColProcess(event, index, colIndex)
                              }}
                              className={classNames(
                                focus ? 'bg-gray-50' : '',
                                'block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900',
                              )}
                            >
                              Delete column
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
                {showColumnDetails.includes(`section-${index}-col-${colIndex}`) && (
                  <div className={'mt-2'}>
                    <div className={'text-xs text-gray-500'}>
                      Content type: <span className={'font-semibold'}>{col.type}</span>
                    </div>
                    {col.type === 'ai-task' && (
                      <div className={'text-xs text-gray-500'}>
                        AI result type: <span className={'font-semibold'}>{col.resultType}</span>
                      </div>
                    )}
                  </div>
                )}
                {showUpdateColumnForm === `section-${index}-column-${colIndex}` && (
                  <UpdateColumnForm section={section} sectionIndex={index} colIndex={colIndex} />
                )}
              </div>
            ))}
          </div>
          {section.data.cols.length < 6 && <AddColumnForm section={section} sectionIndex={index} />}
        </div>
        {section.data.cols.length > 0 && (
          <>
            {section.data.rows.map((row, rowIndex) => {
              const cells = row.cells || row

              return (
                <div key={`section-${index}-row-${rowIndex}`} className={'flex flex-row'}>
                  <div
                    key={`row-${rowIndex}`}
                    className={classNames(
                      `grid gap-2`,
                      section.data.cols.length > 0 && `w-full mr-4`,
                      section.data.cols.length === 1 && `grid-cols-1`,
                      section.data.cols.length === 2 && `grid-cols-2`,
                      section.data.cols.length === 3 && `grid-cols-3`,
                      section.data.cols.length === 4 && `grid-cols-4`,
                      section.data.cols.length === 5 && `grid-cols-5`,
                      section.data.cols.length === 6 && `grid-cols-6`,
                    )}
                  >
                    {cells.map((cell, rowColIndex) => (
                      <button
                        type={'button'}
                        onClick={() =>
                          openUpdateCellContentForm(
                            index,
                            rowIndex,
                            rowColIndex,
                            section.data.cols[rowColIndex].name,
                            cell.assignment ?? '',
                          )
                        }
                        className={
                          'text-left flex items-start align-top col-span-1 rounded-md p-2 py-1 bg-white ring-1 ring-gray-200 text-sm text-gray-700 hover:bg-indigo-50 transition-all'
                        }
                        key={`row-${rowIndex}-col-${rowColIndex}`}
                      >
                        <div className={'line-clamp-3 overflow-hidden'}>
                          {cell.assignment ?? <span className={'text-gray-400'}>Put your content here...</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                  {section.data.cols.length < 6 && (
                    <div className={'w-14 flex items-center justify-center'}>
                      <button
                        type={'button'}
                        onClick={(event) => {
                          deleteRowProcess(event, index, rowIndex)
                        }}
                        className={
                          'h-7 w-8 flex items-center justify-center rounded-md hover:bg-indigo-50 transition-all'
                        }
                      >
                        <TrashIcon className={'w-4 h-4 text-gray-500'} />
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
            <div className={'flex flex-row'}>
              <div
                className={classNames(
                  `grid gap-2`,
                  section.data.cols.length > 0 && `w-full mr-4`,
                  section.data.cols.length === 1 && `grid-cols-1`,
                  section.data.cols.length === 2 && `grid-cols-2`,
                  section.data.cols.length === 3 && `grid-cols-3`,
                  section.data.cols.length === 4 && `grid-cols-4`,
                  section.data.cols.length === 5 && `grid-cols-5`,
                  section.data.cols.length === 6 && `grid-cols-6`,
                )}
              >
                <div className={'col-span-1'}>
                  <button
                    type={'button'}
                    onClick={(event) => {
                      addNewRowProcess(event, index)
                    }}
                    className={
                      'w-full flex flex-row items-center p-2 rounded-md ring-1 ring-gray-200 text-sm text-gray-700 hover:bg-indigo-50 transition-all'
                    }
                  >
                    <PlusIcon className={'w-4 h-4 mr-2'} />
                    <span className={'font-semibold'}>Add new row</span>
                  </button>
                </div>
              </div>
              {section.data.cols.length < 6 && <div className={'w-14'} />}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
